import { WatchQueryFetchPolicy } from '@apollo/client';
import { useNotificationContext } from '@modules/common/components/Notification/NotificationContext';
import {
  IntegrationType,
  IntegrationViewFragment,
  useGetTenantByIdQuery,
} from 'generated/graphql';
import { getIsCohortIntegration } from 'modules/ui/Settings/Integrations/utils/getIsCohortIntegration';
import { useMemo } from 'react';
import { useGetUser } from 'v2/core/services/user';

interface UseFetchConnectedIntegrationsOptions {
  fetchPolicy?: WatchQueryFetchPolicy;
  nextFetchPolicy?: WatchQueryFetchPolicy;
}

export const useFetchConnectedIntegrations = ({
  fetchPolicy = 'cache-and-network',
  nextFetchPolicy = 'cache-first',
}: UseFetchConnectedIntegrationsOptions = {}) => {
  const {
    tenant: { id: tenantID },
  } = useGetUser();

  const { showErrorNotification } = useNotificationContext();

  const { data: tenantData, loading: isLoading } = useGetTenantByIdQuery({
    variables: {
      tenantID,
    },
    onError: () => {
      showErrorNotification({
        contentComponent: 'Failed to fetch integrations',
        props: {},
      });
    },
    fetchPolicy,
    nextFetchPolicy,
  });

  const connectedIntegrations: IntegrationViewFragment[] = useMemo(() => {
    return (
      tenantData?.getTenantByID?.integrations?.map((integration) => {
        if (getIsCohortIntegration(integration.channel.type)) {
          return {
            ...integration,
            integrationType: IntegrationType.Cohorts, // TODO: Remove this when we get integration type for cohorts
          };
        }
        return integration;
      }) ?? []
    );
  }, [tenantData?.getTenantByID?.integrations]);

  return {
    connectedIntegrations,
    isLoading,
  };
};
